import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import '../App.css';
import { fetchRouters, Router } from '../features/routers/routers';
import { networkOfRouter, routerNetworkLabel } from '../features/routers/routerNetwork';
import { RootState } from '../app/store';

const Muted = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" color="text.secondary">{children}</Typography>
);

// Read-only list of routers. Editing happens on the router card
// (/dashboard/routers/:routerId) behind an explicit confirmation step, so a
// stray click or keystroke here can never mutate a router. This used to be
// an editable grid, where it could.
const Routers = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const routersState = useAppSelector((state: RootState) => state.routers);

  useEffect(() => { dispatch(fetchRouters()); }, []);

  const routers = routersState.value ?? [];
  const loading = routersState.status === 'loading' && routers.length === 0;

  const networkChip = (router: Router) => {
    const network = networkOfRouter(router);
    if (network === 'dgtek') return null;
    return <Chip label={routerNetworkLabel(network)} size="small" color="secondary" variant="filled" />;
  };

  const cost = (router: Router) => {
    if (!router.cost || Number(router.cost) === 0) return <Muted>no charge</Muted>;
    return (
      <>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{`$${router.cost}`}</Typography>
        <Typography variant="caption" color="text.secondary">{router.description2 || ''}</Typography>
      </>
    );
  };

  const published = routers.filter((r) => r.published);
  const unpublished = routers.filter((r) => !r.published);

  // One table per group, so what customers can buy is never mixed in with
  // what they cannot. The unpublished one is set back: outlined heading
  // chip, greyed rows.
  const routersTable = (title: string, about: string, rows: Router[]) => {
    const live = rows === published;
    return (
      <Box component="section" aria-label={title}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
          <Chip
            label={rows.length}
            size="small"
            color={live ? 'success' : 'default'}
            variant={live ? 'filled' : 'outlined'}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {about}
        </Typography>
        {rows.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Muted>{live ? 'No router is published.' : 'Nothing here.'}</Muted>
          </Paper>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={live ? { borderLeft: 4, borderLeftColor: 'success.main' } : { bgcolor: 'action.hover' }}
          >
            <Table sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Router</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell>Network</TableCell>
                  <TableCell sx={{ whiteSpace: 'normal' }}>Description</TableCell>
                  <TableCell aria-label="open" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((router) => (
                  <TableRow
                    key={router.id}
                    hover
                    onClick={() => navigate(`/dashboard/routers/${encodeURIComponent(router.id)}`)}
                    sx={{ cursor: 'pointer', opacity: live ? 1 : 0.75 }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {router.name || router.id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`${router.id} · ${router.rsp}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {router.mode ? (
                        <Typography variant="body2">{router.mode}</Typography>
                      ) : <Muted>—</Muted>}
                    </TableCell>
                    <TableCell>{cost(router)}</TableCell>
                    <TableCell>{networkChip(router) ?? <Muted>DGtek</Muted>}</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', maxWidth: 420 }}>
                      {router.description1 ? (
                        <Typography variant="body2">{router.description1}</Typography>
                      ) : <Muted>—</Muted>}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.disabled', width: 40 }}>
                      <ChevronRightIcon fontSize="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{
      maxWidth: 1200, mx: 'auto', mt: 10, px: 2,
    }}
    >
      <Toolbar disableGutters sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1.5} alignItems="baseline">
          <Typography variant="h6">Routers</Typography>
          {routers.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {`${published.length} published · ${routers.length} total`}
            </Typography>
          )}
        </Stack>
        <Button
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/dashboard/routers/new')}
        >
          Add Router
        </Button>
      </Toolbar>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && routers.length === 0 && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>No routers yet.</Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate('/dashboard/routers/new')}
          >
            Add the first router
          </Button>
        </Paper>
      )}

      {!loading && routers.length > 0 && (
        <Stack spacing={4}>
          {routersTable(
            'Published routers',
            'Offered to customers on the signup site.',
            published,
          )}
          {routersTable(
            'Unpublished routers',
            'Drafts and retired routers. Customers do not see these.',
            unpublished,
          )}
        </Stack>
      )}
    </Box>
  );
};

export default Routers;
